import { useEffect, useRef } from 'react';
import { api } from '@/lib/api';
import { useIncidentStore } from '@/stores/useIncidentStore';
import { useStreamStore } from '@/stores/useStreamStore';
import type { IncidentList, IncidentSummary } from '@tiw/shared-types';

/**
 * useIncidents
 *
 * Polls the rolling 24h window `/v1/incidents?window=24h` (real IODA outages +
 * provider status, populated by the Worker's scheduled poller) and reconciles it:
 * **open** incidents drive globe pulses + a red `incident.open` log entry; once
 * **resolved** they emit a green `incident.close` entry and stop pulsing. Each
 * event also lands in the EVENT LOG via the stream store. See docs/BUILD_PLAN.md
 * (P3).
 */
const POLL_MS = 30_000;

export function useIncidents(): void {
  // id → was it resolved last time we saw it.
  const seen = useRef<Map<string, boolean>>(new Map());

  useEffect(() => {
    let cancelled = false;

    const reconcile = (list: readonly IncidentSummary[]): void => {
      const next = new Set(list.map((i) => i.id));
      const incidents = useIncidentStore.getState();
      const stream = useStreamStore.getState();

      const pushClose = (inc: IncidentSummary): void =>
        stream.push({
          id: `${inc.id}:close`,
          channel: 'tiw.incident.close',
          ts: (inc.resolved_at ? Date.parse(inc.resolved_at) : 0) || Date.now(),
          payload: { incident_id: inc.id, title: inc.title },
        });

      for (const inc of list) {
        const isResolved = inc.resolved_at !== null;
        const prev = seen.current.get(inc.id);
        if (prev === undefined) {
          if (isResolved) {
            pushClose(inc); // recently-resolved (backfill / before we watched) — green, no pulse
          } else {
            incidents.open({
              id: inc.id,
              severity: inc.severity,
              title: inc.title,
              source: inc.source,
              lat: inc.lat,
              lon: inc.lon,
            });
            stream.push({
              id: `${inc.id}:open`,
              channel: 'tiw.incident.open',
              ts: Date.parse(inc.opened_at) || Date.now(),
              payload: { incident_id: inc.id, title: inc.title, severity: inc.severity, source: inc.source },
            });
          }
        } else if (!prev && isResolved) {
          // transitioned open → resolved: stop the pulse, log the resolution
          incidents.close(inc.id);
          pushClose(inc);
        }
        seen.current.set(inc.id, isResolved);
      }

      // Aged out of the 24h window — drop any lingering pulse, forget it.
      for (const id of [...seen.current.keys()]) {
        if (next.has(id)) continue;
        incidents.close(id);
        seen.current.delete(id);
      }
    };

    const poll = async (): Promise<void> => {
      try {
        const res = await api.get<IncidentList>('/v1/incidents', { query: { window: '24h' } });
        if (!cancelled) reconcile(res.incidents);
      } catch {
        /* transient — try again on the next tick */
      }
    };

    void poll();
    const timer = setInterval(() => void poll(), POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);
}
